// File: src/sidebar/SidebarContext.js
import {
  createContext,
  useState,
  useContext,
  useMemo,
  useCallback,
  useEffect,
} from "react";

const SidebarContext = createContext(null);

export const SidebarProvider = ({ children }) => {
  const [preferredMinimized, setPreferredMinimized] = useState(() => {
    try {
      return localStorage.getItem("sidebarIsMinimized") === "true";
    } catch {}
    return false;
  });
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 1024,
  );
  const [mobileMinimized, setMobileMinimized] = useState(true);
  useEffect(() => {
    const updateViewport = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);
  // A problem starts focused without changing the learner's desktop preference.
  const [problemSidebar, setProblemSidebar] = useState(null);
  const isProblemRoute = problemSidebar !== null;
  const isMinimized =
    problemSidebar?.isMinimized ??
    (isMobile ? mobileMinimized : preferredMinimized);

  const setProblemRoute = useCallback((routeKey) => {
    if (!routeKey) setMobileMinimized(true);
    setProblemSidebar((previous) => {
      if (!routeKey) return null;
      if (previous?.routeKey === routeKey) return previous;
      return { routeKey, isMinimized: true };
    });
  }, []);

  // Which topic index is expanded in the UI (not the filter)
  const [expandedTopicIndex, setExpandedTopicIndex] = useState(null);

  const [isCourseDropdownOpen, setIsCourseDropdownOpen] = useState(false);

  const toggleSidebar = useCallback(() => {
    if (isProblemRoute) {
      setProblemSidebar((previous) =>
        previous
          ? { ...previous, isMinimized: !previous.isMinimized }
          : previous,
      );
      return;
    }
    if (isMobile) {
      setMobileMinimized((previous) => !previous);
      return;
    }
    setPreferredMinimized((prev) => {
      const next = !prev;
      try {
        if (window.innerWidth >= 1024) {
          localStorage.setItem("sidebarIsMinimized", JSON.stringify(next));
        }
      } catch {}
      return next;
    });
  }, [isProblemRoute, isMobile]);

  // Toggle only: if same index => collapse; else expand that one
  const handleToggleExpand = useCallback(
    (index) => {
      setExpandedTopicIndex((prev) => (prev === index ? null : index));
    },
    [setExpandedTopicIndex],
  );

  // Force expand a specific index (used when clicking a subtopic)
  const expandTopic = useCallback((index) => {
    setExpandedTopicIndex((prev) => (prev === index ? index : index));
  }, []);

  // Collapse all topics
  const collapseAll = useCallback(() => setExpandedTopicIndex(null), []);

  const openCourseDropdown = () => setIsCourseDropdownOpen(true);
  const closeCourseDropdown = () => setIsCourseDropdownOpen(false);

  const value = useMemo(
    () => ({
      isMinimized,
      toggleSidebar,
      setProblemRoute,

      expandedTopicIndex,
      handleToggleExpand,
      expandTopic,
      collapseAll,

      isCourseDropdownOpen,
      openCourseDropdown,
      closeCourseDropdown,
    }),
    [
      isMinimized,
      expandedTopicIndex,
      isCourseDropdownOpen,
      toggleSidebar,
      setProblemRoute,
      handleToggleExpand,
      expandTopic,
      collapseAll,
    ],
  );

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
};

export const useSidebar = () => useContext(SidebarContext);
